import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ShieldAlert, Info } from "lucide-react";

export function IntegrityScoreInfo() {
  return (
    <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="integrity-score" className="border-none">
          <AccordionTrigger className="hover:no-underline py-2">
            <div className="flex items-center gap-3 text-left">
              <ShieldAlert className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-foreground">
                  Understanding Your Integrity Score
                </h3>
                <p className="text-sm text-muted-foreground">
                  Click to learn how your test integrity is measured
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-4 pt-3 pl-8">
              <div className="space-y-2">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  What is the Integrity Score?
                </h4>
                <p className="text-sm text-muted-foreground">
                  Your integrity score starts at <strong className="text-foreground">100</strong> and
                  reflects how closely you followed the test guidelines. Test creators use this score
                  to ensure fair assessment practices.
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-foreground">How is it calculated?</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Points are deducted for the following activities:
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="font-mono text-orange-600 dark:text-orange-400 min-w-[40px]">-10</span>
                    <span>Switching tabs or windows during the test</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-mono text-orange-600 dark:text-orange-400 min-w-[40px]">-15</span>
                    <span>Unusual timing patterns in responses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-mono text-orange-600 dark:text-orange-400 min-w-[40px]">-15</span>
                    <span>Clipboard actions (copy/paste detected)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-mono text-orange-600 dark:text-orange-400 min-w-[40px]">-15</span>
                    <span>Suspicious changes in device or location mid-test</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-foreground">How to maintain a high score:</h4>
                <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                  <li>Stay focused on the test window</li>
                  <li>Don't use external tools or AI assistance</li>
                  <li>Write answers in your own words</li>
                  <li>Complete the test in one sitting from one device</li>
                  <li>Avoid copying content from other sources</li>
                </ul>
              </div>

              <div className="bg-blue-100 dark:bg-blue-900/30 rounded-md p-3 mt-4">
                <p className="text-sm text-foreground">
                  <strong>Note:</strong> A high integrity score demonstrates your commitment
                  to honest assessment and increases test creator confidence in your results.
                </p>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
